import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  Mic,
  Sparkles,
  Camera,
  Music2,
  Crown,
  PawPrint,
  ChevronDown,
  Vote,
  Trophy,
  ShieldCheck,
  Users,
} from "lucide-react";
import Sidebar, { MobileHeader } from "./Sidebar";
import { openAppStore } from "../lib/appStores";

const ACCENT = "#F5A623";

const ROLES = [
  {
    role: "Vocalist",
    description: "Singing challenges designed for vocalists.",
    icon: Mic,
    color: "#F69520",
  },
  {
    role: "Rapper",
    description: "Rap challenges designed for lyricists and performers.",
    icon: Music2,
    color: "#409134",
  },
  {
    role: "Dancer",
    description: "Dance performances and choreography challenges.",
    icon: Sparkles,
    color: "#F95D85",
  },
  {
    role: "Visual",
    description: "Makeup, fashion, and styling showcases.",
    icon: Camera,
    color: "#B24AC0",
  },
  {
    role: "Leader & Personality",
    description: "Personality and leadership challenges.",
    icon: Crown,
    color: "#247CCA",
  },
  {
    role: "Animal Mascot",
    description: "Compete to become the official group mascot.",
    icon: PawPrint,
    color: "#F22600",
  },
];

const STEPS = [
  {
    number: "01",
    title: "Choose Your Role",
    icon: Users,
    body: "Compete in one (or more) of the Roles below. If you qualify for the Final Mission in multiple Roles, you'll choose one Role to continue competing in.",
  },
  {
    number: "02",
    title: "Complete Missions",
    icon: Sparkles,
    body: "Each Role consists of five creative Missions. Every Mission features a new challenge designed specifically for that Role. Mission formats may include individual or collaborative challenges.",
  },
  {
    number: "03",
    title: "Earn Votes",
    icon: Vote,
    body: "After each Mission, the Pagent community votes for their favourite submissions. The highest-ranked Entrants advance to the next Mission. Before every Mission begins, Pagent announces exactly how many Entrants (or what percentage of Entrants) will advance. There are no judges and no random selection. Advancement is determined entirely by community voting.",
  },
  {
    number: "04",
    title: "Become a Finalist",
    icon: ShieldCheck,
    body: "The remaining Entrants after Mission 4 become Finalists. Before competing in the Final Mission, Finalists complete a verification process. If a Finalist has not reached the age of majority in their jurisdiction, a parent or legal guardian must complete a simple verification process before they continue.",
  },
  {
    number: "05",
    title: "Win Your Role",
    icon: Trophy,
    body: "Following the Final Mission, the highest-voted Finalist in each Role becomes the Winner. The five Winners of the Vocalist, Rapper, Dancer, Visual, and Leader & Personality Roles will form the Pagent Girl Group. The highest-voted Animal Mascot will become the official Pagent Girl Group Mascot.",
  },
];

const PRIZES = [
  {
    title: "Guaranteed Cash Prize",
    body: "A guaranteed minimum cash prize of USD $100 for every confirmed Winner.",
  },
  {
    title: "Official Recognition",
    body: "Official recognition on the Pagent platform.",
  },
  {
    title: "Winning Role Title",
    body: "The title associated with their winning Role.",
  },
];

const ELIGIBILITY = [
  "be at least 13 years old;",
  "have a Pagent account in good standing;",
  "comply with the Official Contest Rulebook and Pagent's Terms of Service; and",
  "reside in a jurisdiction where participation is permitted by law.",
];

const FAQS = [
  {
    question: "Is the Contest free?",
    answer:
      "Yes. There is no purchase or payment required to enter or participate.",
  },
  {
    question: "How are Winners selected?",
    answer:
      "Winners are selected entirely through votes cast by the Pagent community.",
  },
  {
    question: "Is there any random draw or lottery?",
    answer:
      "No. There are no random draws, raffles, or games of chance. Advancement and Winners are determined through community voting.",
  },
  {
    question: "Can I compete in more than one Role?",
    answer:
      "Yes. However, if you qualify for the Final Mission in more than one Role, you'll need to choose one Role before competing in the Final Mission.",
  },
  {
    question: "Can I vote for myself?",
    answer: "Yes.",
  },
  {
    question: "Will I always know how many people advance?",
    answer:
      "Yes. Before each Mission begins, Pagent announces the applicable advancement threshold.",
  },
  {
    question: "Can the Contest schedule change?",
    answer:
      "Yes. Mission dates may change as the Contest progresses. Any updates will be announced through the Pagent app.",
  },
];

export default function GirlGroup() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="relative min-h-screen bg-black text-white">
      <Helmet>
        <title>Pagent Girl Group Contest - Pagent</title>
        <meta
          name="description"
          content="Build the world's next girl group. One Mission at a time. Compete through creative Missions for the chance to become part of the official Pagent Girl Group."
        />
        <meta
          property="og:title"
          content="Pagent Girl Group Contest - Pagent"
        />
        <meta
          property="og:description"
          content="Build the world's next girl group. One Mission at a time. Compete for a Role: Vocalist, Rapper, Dancer, Visual, Leader & Personality, or Animal Mascot."
        />
        <meta
          property="og:image"
          content="https://www.pagent.tv/images/girlgroup.png"
        />
        <meta property="og:url" content="https://www.pagent.tv/girlgroup" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Pagent" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Pagent Girl Group Contest - Pagent"
        />
        <meta
          name="twitter:description"
          content="Build the world's next girl group. One Mission at a time. Compete for a Role chosen entirely by community voting."
        />
        <meta
          name="twitter:image"
          content="https://www.pagent.tv/images/girlgroup.png"
        />
        <meta name="theme-color" content="#000000" />
        <link rel="canonical" href="https://www.pagent.tv/girlgroup" />
      </Helmet>

      <Sidebar />

      <div className="min-h-screen md:pl-60">
        <MobileHeader />

        <style>{`
          @keyframes gentle-bounce {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
        `}</style>

        {/* Hero */}
        <section className="px-4 pt-6 sm:pt-10">
          <div className="mx-auto max-w-5xl">
            <div className="relative overflow-hidden rounded-3xl border border-white/10">
              <img
                src="/images/girlgroup.png"
                alt="Pagent Girl Group Contest"
                className="h-[460px] w-full object-cover object-top sm:h-[560px] md:h-[640px]"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black" />

              <div className="pointer-events-none absolute bottom-0 left-1/2 z-20 -translate-x-1/2 translate-y-2">
                <img
                  src="/images/girlgroupmascot.png"
                  alt="Pagent Girl Group Mascot"
                  className="h-48 w-48 object-contain sm:h-60 sm:w-60 md:h-72 md:w-72"
                  style={{
                    filter: "drop-shadow(0 10px 20px rgba(0, 0, 0, 0.5))",
                    animation: "gentle-bounce 6s ease-in-out infinite",
                  }}
                />
              </div>

              <div className="absolute inset-x-0 top-6 z-30 flex justify-center px-6 sm:top-10 md:top-14">
                <div className="space-y-4 text-center">
                  <span
                    className="inline-block rounded-full px-4 py-1 text-xs font-bold uppercase tracking-[0.2em] text-black"
                    style={{ backgroundColor: ACCENT }}
                  >
                    Pagent Contest
                  </span>
                  <h1 className="text-3xl font-black uppercase leading-tight tracking-tight text-white drop-shadow-[0_4px_20px_rgba(0,0,0,0.6)] sm:text-5xl md:text-6xl">
                    Pagent Girl Group
                    <br />
                    Contest
                  </h1>
                  <p className="text-lg font-semibold text-white/80 sm:text-xl">
                    Build the world&apos;s next girl group.
                    <br className="sm:hidden" /> One Mission at a time.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Intro */}
        <section className="px-4 pt-12">
          <div className="mx-auto max-w-3xl space-y-4 text-center">
            <p className="text-lg leading-relaxed text-white/75 sm:text-xl">
              The Pagent Girl Group Contest is a global talent contest
              where creators compete through a series of creative Missions for
              the chance to become part of the official Pagent Girl Group.
            </p>
            <p className="text-base leading-relaxed text-white/60 sm:text-lg">
              Whether you&apos;re a singer, rapper, dancer, entertainer, or just
              have an incredibly talented pet, there&apos;s a Role for you.
            </p>
            <ChevronDown className="mx-auto h-9 w-9 animate-bounce text-white/70" />
          </div>
        </section>

        {/* How it Works */}
        <section className="px-4 pt-12">
          <div className="mx-auto max-w-5xl">
            <h2 className="mb-8 text-center text-2xl font-bold text-white sm:text-3xl">
              How it Works
            </h2>
            <div className="space-y-4">
              {STEPS.map((step) => {
                const Icon = step.icon;
                return (
                  <div
                    key={step.number}
                    className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6"
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl"
                        style={{ backgroundColor: `${ACCENT}22` }}
                      >
                        <Icon className="h-6 w-6" style={{ color: ACCENT }} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="mb-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                          <span
                            className="text-xs font-bold uppercase tracking-[0.2em]"
                            style={{ color: ACCENT }}
                          >
                            Step {step.number}
                          </span>
                          <h3 className="text-lg font-bold text-white sm:text-xl">
                            {step.title}
                          </h3>
                        </div>
                        <p className="text-sm leading-relaxed text-white/70 sm:text-base">
                          {step.body}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Roles */}
        <section className="px-4 pt-16">
          <div className="mx-auto max-w-5xl">
            <h2 className="mb-3 text-center text-2xl font-bold text-white sm:text-3xl">
              Choose Your Role
            </h2>
            <p className="mx-auto mb-8 max-w-2xl text-center text-white/60">
              Compete in one or more Roles. Each Role has five creative Missions
              tailored to that specialty.
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {ROLES.map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.role}
                    className="overflow-hidden rounded-2xl p-px"
                    style={{
                      background: `linear-gradient(135deg, ${item.color}, ${item.color}33)`,
                    }}
                  >
                    <div className="flex h-full flex-col items-center gap-3 rounded-[15px] bg-zinc-950/95 p-5 text-center">
                      <div
                        className="flex h-11 w-11 items-center justify-center rounded-xl"
                        style={{ backgroundColor: `${item.color}22` }}
                      >
                        <Icon
                          className="h-6 w-6"
                          style={{ color: item.color }}
                        />
                      </div>
                      <h3 className="text-base font-bold text-white md:text-lg">
                        {item.role}
                      </h3>
                      <p className="text-sm text-white/60">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Prizes */}
        <section className="px-4 pt-16">
          <div className="mx-auto max-w-3xl">
            <h2 className="mb-3 text-center text-2xl font-bold text-white sm:text-3xl">
              Prizes
            </h2>
            <p className="mb-8 text-center text-white/60">
              Every confirmed Winner receives:
            </p>
            <div className="space-y-4">
              {PRIZES.map((prize, index) => (
                <div
                  key={prize.title}
                  className="rounded-2xl border bg-white/5 p-6"
                  style={{
                    borderColor:
                      index === 0
                        ? "rgba(245, 166, 35, 0.4)"
                        : "rgba(255, 255, 255, 0.1)",
                  }}
                >
                  <h3
                    className="mb-2 text-xl font-bold"
                    style={{ color: index === 0 ? ACCENT : undefined }}
                  >
                    {prize.title}
                  </h3>
                  <p className="text-white/70">{prize.body}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-center text-sm text-white/50">
              As the Contest grows, Pagent may increase prize amounts or
              introduce additional prizes.
            </p>
          </div>
        </section>

        {/* Bigger picture */}
        <section className="px-4 pt-16">
          <div className="mx-auto max-w-3xl">
            <h2 className="mb-6 text-center text-2xl font-bold text-white sm:text-3xl">
              Could This Become Something Bigger?
            </h2>
            <div className="space-y-4 rounded-2xl border border-white/10 bg-white/5 p-6 sm:p-8">
              <p className="text-lg font-semibold text-white">We hope so.</p>
              <p className="leading-relaxed text-white/70">
                If the Contest generates strong community support and one or
                more Winners are interested in pursuing opportunities beyond the
                Contest, Pagent may explore opportunities within the music
                and entertainment industries.
              </p>
              <p className="leading-relaxed text-white/70">
                These opportunities could include conversations with record
                labels, producers, managers, sponsors, or other industry
                professionals.
              </p>
              <p className="leading-relaxed text-white/50">
                As a growing company, we can&apos;t guarantee the scale of these
                opportunities, but we&apos;re committed to pursuing them. Any
                future opportunities would always be voluntary and subject to
                separate discussions and agreements.
              </p>
            </div>
          </div>
        </section>

        {/* Eligibility */}
        <section className="px-4 pt-16">
          <div className="mx-auto max-w-3xl">
            <h2 className="mb-6 text-center text-2xl font-bold text-white sm:text-3xl">
              Eligibility
            </h2>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 sm:p-8">
              <p className="mb-4 font-semibold text-white">
                To participate, you must:
              </p>
              <ul className="mb-6 list-disc space-y-2 pl-5 text-white/70">
                {ELIGIBILITY.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <p className="leading-relaxed text-white/60">
                Entrants under the age of majority are welcome to compete. If
                they qualify for the Final Mission, a parent or legal guardian
                must complete Pagent&apos;s verification process before they can
                continue.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="px-4 pt-16">
          <div className="mx-auto max-w-3xl">
            <h2 className="mb-8 text-center text-2xl font-bold text-white sm:text-3xl">
              Frequently Asked Questions
            </h2>
            <div className="space-y-3">
              {FAQS.map((faq, index) => {
                const isOpen = openFaq === index;
                return (
                  <div
                    key={faq.question}
                    className="overflow-hidden rounded-2xl border border-white/10 bg-zinc-900/60"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : index)}
                      className="flex w-full items-center justify-between px-6 py-4 text-left transition-colors hover:bg-white/5"
                    >
                      <h3 className="pr-4 text-base font-semibold text-white sm:text-lg">
                        {faq.question}
                      </h3>
                      <ChevronDown
                        className={`h-5 w-5 shrink-0 transition-transform duration-200 ${
                          isOpen ? "rotate-180" : ""
                        }`}
                        style={{ color: ACCENT }}
                      />
                    </button>
                    {isOpen && (
                      <div className="border-t border-white/10 px-6 py-4">
                        <p className="leading-relaxed text-white/70">
                          {faq.answer}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Rulebook */}
        <section className="px-4 pt-16">
          <div className="mx-auto max-w-3xl rounded-2xl border border-white/10 bg-white/5 p-6 text-center sm:p-8">
            <h2 className="mb-3 text-xl font-bold text-white sm:text-2xl">
              Official Contest Rulebook
            </h2>
            <p className="mb-6 leading-relaxed text-white/60">
              This page is intended to provide a general overview of the
              Contest. For complete details, including eligibility
              requirements, voting rules, prizes, content rights, and legal
              terms, please refer to the Official Contest Rulebook.
            </p>
            <Link
              to="/girlgroup/rulebook"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-black transition-opacity hover:opacity-90"
              style={{ backgroundColor: ACCENT }}
            >
              Read the Official Rulebook
            </Link>
          </div>
        </section>

        {/* CTA */}
        <section className="px-4 py-20">
          <div className="mx-auto max-w-3xl space-y-7 text-center">
            <h2 className="text-3xl font-bold text-white sm:text-4xl">
              Ready to Compete?
            </h2>
            <p className="text-lg text-white/70">
              Download the Pagent app, choose your Role, and start completing
              Missions. Your journey begins today.
            </p>
            <button
              type="button"
              className="inline-flex items-center gap-3 rounded-full px-10 py-4 text-lg font-bold text-black shadow-2xl transition-opacity hover:opacity-90"
              style={{ backgroundColor: ACCENT }}
              onClick={openAppStore}
            >
              Enter on the Pagent App
            </button>
          </div>
        </section>

        <footer className="border-t border-white/10 px-4 py-8 text-center text-sm text-white/40">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            <Link to="/terms" className="hover:text-white/70">
              Terms
            </Link>
            <Link to="/privacy" className="hover:text-white/70">
              Privacy
            </Link>
            <Link to="/faq" className="hover:text-white/70">
              FAQ
            </Link>
            <Link to="/updates" className="hover:text-white/70">
              Patch updates
            </Link>
            <Link to="/support" className="hover:text-white/70">
              Support
            </Link>
          </div>
          <p className="mt-3">© 2026 Pagent. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}
